import { heicTo } from 'heic-to';

/**
 * Check if a file is HEIC/HEIF format
 */
export const isHeicFile = (file: File): boolean =>
  file.type === 'image/heic' ||
  file.type === 'image/heif' ||
  file.name.toLowerCase().endsWith('.heic') ||
  file.name.toLowerCase().endsWith('.heif');

/**
 * Check if a file is AVIF format. iPhones y cámaras modernas exportan AVIF
 * y varios pipelines (thumbnails, ffmpeg server-side, previews) no lo
 * manejan bien todavía, así que lo convertimos a JPEG antes de subirlo.
 */
export const isAvifFile = (file: File): boolean =>
  file.type === 'image/avif' ||
  file.name.toLowerCase().endsWith('.avif');

/**
 * Formatos que el visor, los thumbnails y los pipelines de IA no saben
 * renderizar/procesar (RAW de cámara + TIFF). No los convertimos: los
 * rechazamos antes de subir para que el usuario elija un formato estándar.
 * Detección por extensión porque los RAW suelen llegar con MIME vacío o poco
 * fiable (a diferencia de HEIC/AVIF, que sí traen `type` consistente).
 */
export const UNSUPPORTED_UPLOAD_EXTENSIONS = [
  'dng',
  'raw',
  'cr2',
  'nef',
  'arw',
  'tiff',
  'tif',
  // GIF y SVG: el atlas-backend no los acepta y no vale la pena convertirlos
  // (ADR-590) — se bloquean con el mismo mensaje que RAW/TIFF.
  'gif',
  'svg',
] as const;

/** Mensaje único que ve el usuario al intentar subir un formato bloqueado. */
export const UNSUPPORTED_FORMAT_MESSAGE =
  'Ese formato no es compatible. Subí una imagen en JPG o PNG.';

/** true si el archivo es WEBP. Desde ADR-589 el backend NO acepta webp:
 *  se convierte a JPEG antes de subir (nunca se bloquea — la conversión es
 *  nativa del browser). */
export const isWebpFile = (file: File): boolean =>
  file.type === 'image/webp' || file.name.toLowerCase().endsWith('.webp');

/**
 * Error tipado para distinguir "formato no soportado" de un fallo real de
 * conversión. Los call-sites que hoy re-etiquetan errores de conversión a un
 * mensaje genérico pueden chequear `instanceof` y re-mostrar este mensaje
 * específico en su lugar.
 */
export class UnsupportedImageFormatError extends Error {
  constructor(message: string = UNSUPPORTED_FORMAT_MESSAGE) {
    super(message);
    this.name = 'UnsupportedImageFormatError';
  }
}

/** true si el archivo es un formato de imagen que NO aceptamos (RAW/TIFF). */
export const isUnsupportedUploadFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  const ext = name.includes('.') ? name.split('.').pop() ?? '' : '';
  return (UNSUPPORTED_UPLOAD_EXTENSIONS as readonly string[]).includes(ext);
};

/**
 * Lanza `UnsupportedImageFormatError` si el archivo es un formato bloqueado.
 * Usar en los call-sites que no pasan por `normalizeImageForUpload` (ej. el
 * preview de un picker) o que tienen su propio gate de tipo previo, para que
 * el rechazo muestre el mismo mensaje en todos lados.
 */
export const assertSupportedUploadFormat = (file: File): void => {
  if (isUnsupportedUploadFile(file)) {
    throw new UnsupportedImageFormatError();
  }
};

/**
 * Convierte un archivo de imagen a JPEG usando el decoder NATIVO del browser
 * (offscreen `<img>` + canvas). Sirve para cualquier formato que el browser
 * decodifique (AVIF: Chrome 85+/Safari 16+/Firefox 93+; WEBP: todos).
 * Pinta fondo BLANCO antes de dibujar: sin eso, la transparencia de un
 * webp/avif quedaba NEGRA al pasar a JPEG (fondo correcto para fotos de
 * producto).
 */
const convertViaCanvasToJpeg = async (
  file: File,
  extRegex: RegExp,
  quality: number,
  formatoLabel: string,
): Promise<{ file: File; url: string }> => {
  const srcUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () =>
        reject(
          new Error(`El navegador no pudo decodificar la imagen ${formatoLabel}`),
        );
      el.src = srcUrl;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No se pudo crear un contexto 2D para convertir la imagen');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    const jpegBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        b => (b ? resolve(b) : reject(new Error('toBlob devolvió null'))),
        'image/jpeg',
        quality,
      );
    });
    const newName = file.name.replace(extRegex, '.jpg');
    const newFile = new File([jpegBlob], newName, { type: 'image/jpeg' });
    return { file: newFile, url: URL.createObjectURL(jpegBlob) };
  } finally {
    URL.revokeObjectURL(srcUrl);
  }
};

/**
 * Convert a HEIC/HEIF — y desde ADR-589 también WEBP — file to JPEG.
 * Returns { file, url } where url is a blob URL for preview. For other
 * formats, returns the original file with a blob URL. Los call-sites legacy
 * gatean con `isHeicFile(f) || isWebpFile(f)` y llaman esto — la conversión
 * correcta se elige acá adentro.
 */
export const convertHeicToJpeg = async (
  file: File,
  quality = 0.85
): Promise<{ file: File; url: string }> => {
  if (isHeicFile(file)) {
    const jpegBlob = await heicTo({ blob: file, type: 'image/jpeg', quality });
    const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    const newFile = new File([jpegBlob], newName, { type: 'image/jpeg' });
    return { file: newFile, url: URL.createObjectURL(jpegBlob) };
  }
  if (isWebpFile(file)) {
    return convertViaCanvasToJpeg(file, /\.webp$/i, quality, 'WEBP');
  }
  return { file, url: URL.createObjectURL(file) };
};

/**
 * Convert an AVIF file to JPEG using the browser's native AVIF decoder.
 */
export const convertAvifToJpeg = async (
  file: File,
  quality = 0.85,
): Promise<{ file: File; url: string }> => {
  if (!isAvifFile(file)) {
    return { file, url: URL.createObjectURL(file) };
  }
  return convertViaCanvasToJpeg(file, /\.avif$/i, quality, 'AVIF');
};

/**
 * Convert a WEBP file to JPEG (el backend nuevo no acepta webp, ADR-589).
 */
export const convertWebpToJpeg = async (
  file: File,
  quality = 0.85,
): Promise<{ file: File; url: string }> => {
  if (!isWebpFile(file)) {
    return { file, url: URL.createObjectURL(file) };
  }
  return convertViaCanvasToJpeg(file, /\.webp$/i, quality, 'WEBP');
};

/**
 * Normaliza un archivo de imagen antes de subirlo: convierte HEIC/HEIF/AVIF
 * y WEBP (el backend nuevo no lo acepta, ADR-589) a JPEG y deja el resto
 * intacto. Es un no-op para formatos que el backend y el visor ya manejan
 * (jpeg/png/gif/svg). Pensado para usarse dentro de los helpers de upload,
 * así cada call-site no tiene que saber qué formato aceptar.
 */
export const normalizeImageForUpload = async (file: File): Promise<File> => {
  // Rechazar formatos no soportados (RAW/TIFF) antes de cualquier conversión.
  assertSupportedUploadFormat(file);
  // Los conversores devuelven `{ file, url }` porque los pickers usan ese url
  // como preview. Acá solo interesa el File, así que hay que REVOCAR el url:
  // si no, cada archivo convertido deja un blob colgado. Se notaba poco con
  // uploads de a uno, pero el alta en bloque manda hasta 20 por tanda
  // (ADR-687).
  const take = ({ file: converted, url }: { file: File; url: string }): File => {
    URL.revokeObjectURL(url);
    return converted;
  };
  if (isHeicFile(file)) {
    return take(await convertHeicToJpeg(file, 0.85));
  }
  if (isAvifFile(file)) {
    return take(await convertAvifToJpeg(file, 0.85));
  }
  if (isWebpFile(file)) {
    return take(await convertWebpToJpeg(file, 0.85));
  }
  return file;
};
